import * as RadixAccordion from '@radix-ui/react-accordion';
import { Button, Dialog } from '@scholastic/mockingjay';
import { type ReactNode } from 'react';
import CaretRightIcon from '@roster/assets/icons/caret-right.svg';
import GoogleImportErrorIcon from '@roster/assets/icons/google-import-error.svg';
import GoogleImportSuccessIcon from '@roster/assets/icons/google-import-success.svg';
import {
  GOOGLE_CLASS_NOT_IMPORTED_ERROR_CODE,
  groupImportGoogleClassesStudentErrors,
  type ImportGoogleClassesApiResponse,
  type ImportGoogleClassesClassSyncFailure,
} from '../../hooks/api/use-import-google-classes-api/use-import-google-classes-api';

const hiddenTrigger = (
  <button type="button" className="sr-only" tabIndex={-1} aria-hidden>
    Open Google classes import issues dialog
  </button>
);

const BUTTON_PADDING_CLASS_NAME = 'py-2.5';

interface ImportGoogleClassesIssuesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: ImportGoogleClassesApiResponse | null;
  title?: string;
  action?: 'import' | 'sync';
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

function getStudentChangeSummary(
  studentsAddedCount = 0,
  studentsRemovedCount = 0
) {
  const summaryParts = [
    studentsAddedCount > 0
      ? `${studentsAddedCount} ${pluralize(
          studentsAddedCount,
          'student'
        )} added`
      : null,
    studentsRemovedCount > 0
      ? `${studentsRemovedCount} ${pluralize(
          studentsRemovedCount,
          'student'
        )} removed`
      : null,
  ].filter(Boolean);

  return summaryParts.join(', ');
}

const OWNER_IMPORT_REQUIRED_MESSAGE =
  'You cannot import these classes until the class owner has imported them first. Please contact the class owner to complete their class import.';

function ErrorMessage({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-1 text-sm font-medium leading-[1.5] text-[#DB0C0C]">
      {/* One line tall, so the icon centers on the first line when the text wraps. */}
      <span className="flex h-[1.5em] shrink-0 items-center">
        <GoogleImportErrorIcon className="size-4" />
      </span>
      {children}
    </p>
  );
}

const ACCORDION_TRIGGER_CLASS_NAME =
  'group flex items-center gap-3 rounded-[4px] bg-white p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#191918] focus-visible:ring-offset-2';

const ACCORDION_TRIGGER_LABEL_CLASS_NAME =
  'flex min-h-10 items-center gap-1 text-base font-bold leading-6 text-[#313131]';

const ACCORDION_CARET_CLASS_NAME =
  'size-4 shrink-0 text-[#191918] transition-transform duration-200 group-data-[state=open]:rotate-90';

function ImportAccordionSection({
  value,
  icon,
  label,
  children,
}: {
  value: string;
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <RadixAccordion.Item value={value}>
      <RadixAccordion.Header className="m-0">
        <RadixAccordion.Trigger className={ACCORDION_TRIGGER_CLASS_NAME}>
          <CaretRightIcon aria-hidden className={ACCORDION_CARET_CLASS_NAME} />
          <span className={ACCORDION_TRIGGER_LABEL_CLASS_NAME}>
            {icon}
            {label}
          </span>
        </RadixAccordion.Trigger>
      </RadixAccordion.Header>
      <RadixAccordion.Content className="pt-1 text-[#191918]">
        <div className="rounded-lg border border-[#D4D4D4] bg-white px-3 py-2">
          {children}
        </div>
      </RadixAccordion.Content>
    </RadixAccordion.Item>
  );
}

function ClassErrorGroup({
  message,
  classErrors,
}: {
  message: string;
  classErrors: ImportGoogleClassesClassSyncFailure[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <ErrorMessage>{message}</ErrorMessage>
      <div className="flex flex-col gap-2 px-6">
        {classErrors.map((classError) => (
          <p
            key={classError.className}
            className="break-words text-base font-medium leading-6 text-[#191918]"
          >
            {classError.className}
          </p>
        ))}
      </div>
    </div>
  );
}

function isOwnerImportRequired(
  classError: ImportGoogleClassesClassSyncFailure
) {
  return classError.errorDetail?.code === GOOGLE_CLASS_NOT_IMPORTED_ERROR_CODE;
}

function ClassErrorGroups({
  classErrors,
}: {
  classErrors: ImportGoogleClassesClassSyncFailure[];
}) {
  const ownerImportErrors = classErrors.filter(isOwnerImportRequired);
  const otherClassErrors = classErrors.filter(
    (classError) => !isOwnerImportRequired(classError)
  );

  return (
    <div className="flex flex-col gap-8">
      {ownerImportErrors.length > 0 ? (
        <ClassErrorGroup
          message={OWNER_IMPORT_REQUIRED_MESSAGE}
          classErrors={ownerImportErrors}
        />
      ) : null}
      {otherClassErrors.length > 0 ? (
        <ClassErrorGroup
          message="Check these class names in Google Classroom and try again."
          classErrors={otherClassErrors}
        />
      ) : null}
    </div>
  );
}

export function ImportGoogleClassesIssuesDialog({
  open,
  onOpenChange,
  result,
  title = 'Import issues',
  action = 'import',
}: ImportGoogleClassesIssuesDialogProps) {
  const successVerb = action === 'sync' ? 'synced' : 'imported';
  const failedVerb = action === 'sync' ? 'failed to sync' : 'failed to import';
  const successHeading =
    action === 'sync' ? 'Synced classes:' : 'Imported classes:';
  const syncedClasses = result?.success?.classesSynced ?? [];
  const classErrors = result?.errors?.classErrors ?? [];
  const studentErrors = result?.errors?.studentErrors ?? [];
  const groupedStudentErrors =
    groupImportGoogleClassesStudentErrors(studentErrors);
  const defaultOpenItems = [
    classErrors.length > 0 ? 'class-errors' : null,
    studentErrors.length > 0 ? 'student-errors' : null,
  ].filter(Boolean) as string[];

  if (!result || defaultOpenItems.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={hiddenTrigger}
      title={title}
      className="tablet:px-8 tablet:pb-8 flex max-h-[80dvh] w-[calc(100vw-3rem)] max-w-[700px] flex-col p-6 pt-10"
    >
      {/* Scroll the body, not the panel, so the title, close X and Close button stay in view. */}
      <div className="-mx-1 mt-7 min-h-0 overflow-y-auto p-1">
        <RadixAccordion.Root
          type="multiple"
          className="flex flex-col gap-6"
          defaultValue={defaultOpenItems}
        >
          {syncedClasses.length > 0 ? (
            <ImportAccordionSection
              value="success"
              icon={
                <GoogleImportSuccessIcon className="size-6 shrink-0 text-[#007F17]" />
              }
              label={`${syncedClasses.length} ${pluralize(
                syncedClasses.length,
                'class',
                'classes'
              )} ${successVerb}`}
            >
              <div className="flex flex-col gap-2">
                <p className="text-base font-medium leading-6 text-[#4D4D4D]">
                  {successHeading}
                </p>
                <div className="flex flex-col gap-2">
                  {syncedClasses.map((syncedClass) => {
                    const studentChangeSummary = getStudentChangeSummary(
                      syncedClass.studentsAddedCount,
                      syncedClass.studentsRemovedCount
                    );

                    return (
                      <div
                        key={syncedClass.className}
                        className="flex flex-col gap-1"
                      >
                        <p className="break-words text-base font-medium leading-6 text-[#191918]">
                          {syncedClass.className}
                        </p>
                        {studentChangeSummary ? (
                          <p className="text-sm font-medium leading-6 text-[#4D4D4D]">
                            {studentChangeSummary}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </ImportAccordionSection>
          ) : null}

          {classErrors.length > 0 ? (
            <ImportAccordionSection
              value="class-errors"
              icon={<GoogleImportErrorIcon className="size-6 shrink-0" />}
              label={`${classErrors.length} ${pluralize(
                classErrors.length,
                'class',
                'classes'
              )} ${failedVerb}`}
            >
              <ClassErrorGroups classErrors={classErrors} />
            </ImportAccordionSection>
          ) : null}

          {studentErrors.length > 0 ? (
            <ImportAccordionSection
              value="student-errors"
              icon={<GoogleImportErrorIcon className="size-6 shrink-0" />}
              label={`${studentErrors.length} ${pluralize(
                studentErrors.length,
                'student'
              )} ${failedVerb}`}
            >
              <div className="flex flex-col gap-2">
                <ErrorMessage>
                  Check the student names in Google Classroom and try again.
                </ErrorMessage>
                <div className="flex max-h-[160px] flex-col gap-4 overflow-y-auto">
                  {groupedStudentErrors.map((studentErrorGroup) => (
                    <div
                      key={studentErrorGroup.className}
                      className="flex flex-col gap-1"
                    >
                      <p className="break-words text-xs font-medium leading-[1.5] text-[#6D6D6D]">
                        {studentErrorGroup.className}
                      </p>
                      <div className="flex flex-col gap-2">
                        {studentErrorGroup.students.map((studentError) => (
                          <p
                            key={`${studentErrorGroup.className}-${studentError.studentName}`}
                            className="break-words text-base font-medium leading-6 text-[#191918]"
                          >
                            {studentError.studentName}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ImportAccordionSection>
          ) : null}
        </RadixAccordion.Root>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          variant="primary"
          className={BUTTON_PADDING_CLASS_NAME}
          onClick={() => onOpenChange(false)}
        >
          Close
        </Button>
      </div>
    </Dialog>
  );
}

export type { ImportGoogleClassesIssuesDialogProps };
